export const WEB_VITAL_NAMES = ['CLS', 'FCP', 'FID', 'INP', 'LCP', 'TTFB'] as const;

export type WebVitalName = typeof WEB_VITAL_NAMES[number];
export type TelemetryEventType = 'page_view' | 'web_vital' | 'client_error';

export type TelemetryEvent = {
    schemaVersion: 1;
    type: TelemetryEventType;
    name: string;
    route: string;
    value?: number;
    rating?: 'good' | 'needs-improvement' | 'poor';
};

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const OPAQUE_SEGMENT = /^[A-Za-z0-9_-]{24,}$/;

export function normalizeTelemetryRoute(value: string): string {
    const pathname = value.split(/[?#]/, 1)[0] || '/';
    if (!pathname.startsWith('/')) return '/unknown';

    const segments = pathname.split('/').map((segment, index, all) => {
        if (!segment || index === 0) return segment;
        const parent = all[index - 1];
        if (parent === 'chat' || parent === 'puzzle') return '[id]';
        if (UUID_SEGMENT.test(segment) || OPAQUE_SEGMENT.test(segment)) return '[id]';
        return segment.replace(/[^\p{L}\p{N}._~-]/gu, '').slice(0, 32) || '[segment]';
    });

    return segments.join('/').slice(0, 160) || '/';
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function safeName(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const name = value.trim();
    return /^[A-Za-z][A-Za-z0-9._-]{0,63}$/.test(name) ? name : null;
}

export function parseTelemetryEvent(value: unknown): TelemetryEvent | null {
    if (!isRecord(value) || value.schemaVersion !== 1) return null;
    if (value.type !== 'page_view' && value.type !== 'web_vital' && value.type !== 'client_error') return null;

    const name = safeName(value.name);
    const route = typeof value.route === 'string' ? normalizeTelemetryRoute(value.route) : null;
    if (!name || !route) return null;

    if (value.type === 'page_view' && name !== 'page_view') return null;
    if (value.type === 'web_vital' && !WEB_VITAL_NAMES.includes(name as WebVitalName)) return null;

    const event: TelemetryEvent = {
        schemaVersion: 1,
        type: value.type,
        name,
        route,
    };

    if (value.type === 'web_vital') {
        if (typeof value.value !== 'number' || !Number.isFinite(value.value)) return null;
        event.value = Math.max(-1_000_000_000, Math.min(1_000_000_000, value.value));
        if (value.rating === 'good' || value.rating === 'needs-improvement' || value.rating === 'poor') {
            event.rating = value.rating;
        }
    }

    return event;
}
